/**
 * Platform runtime helper.
 *
 * Reads `platform.config.json` at build time and exposes typed accessors
 * for branding, theming, integrations and feature flags. Also injects the
 * theme CSS variables onto `:root` at boot, so the whole UI re-skins from
 * a single config file with no hardcoded brand colors.
 */

import platformConfig from '../../../../platform.config.json'

export interface PlatformConfig {
  brand: {
    name: string
    productName: string
    legalName: string
    description: string
    tagline: string
    supportEmail: string
    emailFrom: string
    social: {
      twitter?: string
      linkedin?: string
      instagram?: string
      github?: string
      facebook?: string
    }
  }
  instance: {
    slug: string
    language: string
    supportedLanguages: string[]
    currencyTicker: string
  }
  domain: {
    appUrl: string
    adminApiUrl: string
    realtimeUrl: string
  }
  theme: {
    primaryColor: string
    /**
     * Secondary brand color used as accent (chip highlights, gold gradients,
     * KPI emphasis, etc.). Optional — falls back to gold (#F59E0B) if absent.
     */
    accentColor?: string
    radius: string
    fonts: { sans: string; mono: string }
    darkMode: { enabled: boolean; default: 'light' | 'dark' | 'system' }
    logos: { primary: string; negative: string; circular: string; favicon: string }
  }
  marketing: {
    stats: { tvl: string; investors: string; secured: string }
    team?: Array<{
      name: string
      roleKey: string
      image: string
      initials: string
      linkedin: string
      email: string
    }>
  }
  blockchain: {
    chainId: number
    chainName: string
    rpcUrl: string
    blockExplorer: string
    usdcAddress: string
    factoryAddress: string
    kycRegistryAddress: string
  }
  integrations: {
    stripe: { enabled: boolean; publishableKey: string }
    cdp: { enabled: boolean; projectId: string }
    resend: { apiKey: string; from: string }
    bigAdmin: { url: string; orgId: string }
  }
  kyc: {
    activeProvider: string
    providers: Record<string, KycProviderConfig>
  }
  /**
   * Política de correos aceptados en el login. El servidor es la autoridad
   * (apps/api/src/auth/email-policy.ts); el front solo la replica para avisar
   * antes de enviar. Si falta el bloque se asume la lista histórica de Gmail.
   */
  auth?: {
    /** Dominios aceptados. `["*"]` o `[]` aceptan cualquiera. */
    allowedEmailDomains?: string[]
    normalizeGoogleAliases?: boolean
  }
  features: Record<string, boolean>
}

/** Per-provider KYC configuration (config-driven, modular). */
export interface KycProviderConfig {
  enabled: boolean
  mode: 'inapp' | 'redirect'
  label?: string
  workflowId?: string
  /** Backend endpoints (relative to domain.adminApiUrl) for redirect providers. */
  sessionEndpoint?: string
  statusEndpoint?: string
  /** SSE endpoint the web app subscribes to for live webhook-driven status updates. */
  eventsEndpoint?: string
}

const baseConfig = platformConfig as unknown as PlatformConfig

export const platform: PlatformConfig = {
  ...baseConfig,
  domain: {
    ...baseConfig.domain,
    appUrl: import.meta.env.VITE_APP_URL
      ? import.meta.env.VITE_APP_URL
      : baseConfig.domain.appUrl,
    adminApiUrl: import.meta.env.VITE_API_URL
      ? import.meta.env.VITE_API_URL
      : baseConfig.domain.adminApiUrl
  }
}

// --- Color helpers ---------------------------------------------------------

/** Convert `#RRGGBB` (or `#RGB`) to `H S% L%` triplet for Tailwind hsl() vars. */
export function hexToHsl(hex: string): string {
  let r = 0
  let g = 0
  let b = 0
  if (hex.length === 4) {
    r = parseInt(hex[1]! + hex[1]!, 16)
    g = parseInt(hex[2]! + hex[2]!, 16)
    b = parseInt(hex[3]! + hex[3]!, 16)
  } else if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16)
    g = parseInt(hex.substring(3, 5), 16)
    b = parseInt(hex.substring(5, 7), 16)
  }
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0
  let s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

/** Returns a readable foreground HSL triplet (near-white or near-black) using YIQ contrast. */
export function getReadableForegroundHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 128 ? '30 2% 16%' : '0 0% 100%'
}

// --- Runtime theme injection ----------------------------------------------

/**
 * Injects the platform theme onto `:root` as CSS variables.
 * Called once from main.tsx so the entire UI picks up the config-driven palette.
 */
export function applyPlatformTheme(config: PlatformConfig = platform): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const primaryHsl = hexToHsl(config.theme.primaryColor)
  const primaryFg = getReadableForegroundHsl(config.theme.primaryColor)

  root.style.setProperty('--primary', primaryHsl)
  root.style.setProperty('--primary-foreground', primaryFg)
  root.style.setProperty('--accent', primaryHsl)
  root.style.setProperty('--ring', primaryHsl)
  root.style.setProperty('--brand-primary', primaryHsl)
  root.style.setProperty('--brand-primary-foreground', primaryFg)

  // Optional secondary brand accent (gold by default). Falls back gracefully
  // so existing configs without `accentColor` keep working.
  const accentHex = config.theme.accentColor ?? '#F59E0B'
  const accentHsl = hexToHsl(accentHex)
  const accentFg = getReadableForegroundHsl(accentHex)
  root.style.setProperty('--brand-accent', accentHsl)
  root.style.setProperty('--brand-accent-foreground', accentFg)

  root.style.setProperty('--radius', config.theme.radius)
  root.style.setProperty('--font-sans', config.theme.fonts.sans)
  root.style.setProperty('--font-mono', config.theme.fonts.mono)
}

/** Apply the initial dark/light mode preference from config. */
export function applyInitialColorScheme(config: PlatformConfig = platform): void {
  if (typeof document === 'undefined') return
  const pref = config.theme.darkMode.default
  let mode: 'light' | 'dark' = 'light'
  if (pref === 'dark') mode = 'dark'
  else if (pref === 'system') {
    mode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  document.documentElement.classList.toggle('dark', mode === 'dark')
}

/** One-shot bootstrap called from main.tsx. */
export function initializePlatform(): void {
  applyPlatformTheme()
  applyInitialColorScheme()
  if (typeof document !== 'undefined') {
    document.documentElement.lang = platform.instance.language
  }
}

// --- Convenience accessors -------------------------------------------------

export const brand = platform.brand
export const theme = platform.theme
export const integrations = platform.integrations
export const features = platform.features
export const blockchain = platform.blockchain
export const kyc = platform.kyc
